// ============================================================
// Summit League v2 — Season Header Component
// Hero banner: logo | game name + season info | status badge
// ============================================================

import React from 'react';
import type { SeasonHeader } from '../../types/dashboard.types';
import {
  formatDate,
  getStatusLabel,
  getStatusClass,
  getStatusNote,
} from './DashboardHelpers';

import SummitLeagueLogo from '../../assets/summit-league-logo.png';

// ── Props ──────────────────────────────────────────────────

interface SeasonHeaderProps {
  season: SeasonHeader | null;
  loading: boolean;
}

// ── Component ──────────────────────────────────────────────

const SeasonHeaderSection: React.FC<SeasonHeaderProps> = ({ season, loading }) => {
  // Loading skeleton
  if (loading) {
    return (
      <div className="sl-card sl-season-card sl-season-card--hero">
        <div className="sl-season-card-glow" />
        <div className="sl-season-hero">
          <div className="sl-season-hero-logo">
            <div className="sl-season-hero-logo-inner sl-skeleton-pulse" />
          </div>
          <div className="sl-season-hero-main">
            <div className="sl-skeleton-line sl-skeleton-line--sm sl-skeleton-pulse" />
            <div className="sl-skeleton-line sl-skeleton-line--lg sl-skeleton-pulse" />
            <div className="sl-skeleton-line sl-skeleton-line--md sl-skeleton-pulse" />
          </div>
          <div className="sl-skeleton-box sl-skeleton-box--status sl-skeleton-pulse" />
        </div>
      </div>
    );
  }

  // No active season
  if (!season) {
    return (
      <div className="sl-card sl-season-card sl-season-card--hero">
        <div className="sl-season-card-glow" />
        <div
          className="sl-season-hero-main"
          style={{ padding: '2rem 0', textAlign: 'center' }}
        >
          <h2 className="sl-season-title">No Active Season</h2>
          <p className="sl-season-subtitle">
            Check back soon — a new season is on the horizon.
          </p>
        </div>
      </div>
    );
  }

  const startFormatted = formatDate(season.startDate);
  const endFormatted = formatDate(season.endDate);
  const statusLabel = getStatusLabel(season.status);
  const statusClass = getStatusClass(season.status);

  return (
    <div className="sl-card sl-season-card sl-season-card--hero">
      <div className="sl-season-card-glow" />

      <div className="sl-season-hero">
        {/* Left: Logo */}
        <div className="sl-season-hero-logo">
          <div className="sl-season-hero-logo-inner">
            <img
              src={season.bannerUrl || season.gameLogo || SummitLeagueLogo}
              alt={season.gameName}
              className="sl-season-hero-logo-img"
            />
          </div>
        </div>

        {/* Center: Info */}
        <div className="sl-season-hero-main">
          <span className="sl-season-tagline">
            Summit League &bull; {season.gameName}
          </span>
          <h1 className="sl-season-title">{season.gameName}</h1>
          <span className="sl-season-subtitle">{season.seasonName}</span>
          <p className="sl-season-meta-primary">
            {startFormatted} – {endFormatted}
            {season.status === 'completed' || season.status === 'archived'
              ? ''
              : ` \u00b7 ${season.daysLeft} days left`}
          </p>
          <p className="sl-season-meta-secondary">
            4v4 Ladder &middot; Min {season.minTeamSize} players &middot; Max{' '}
            {season.maxTeamSize} players per team
          </p>

          {/* Mobile-only inline status */}
          <div className="sl-season-status-inline">
            <span className="sl-season-status-inline-label">Season Status</span>
            <span className={`sl-season-status-main ${statusClass}`}>
              {statusLabel}
            </span>
            <span className="sl-season-status-inline-note">
              {getStatusNote(season.status)}
            </span>
          </div>
        </div>

        {/* Right: Status box (desktop) */}
        <div className="sl-season-status">
          <span className="sl-season-status-label">Season Status</span>
          <span className={`sl-season-status-main ${statusClass}`}>
            {statusLabel}
          </span>
          <span className="sl-season-status-note">
            {getStatusNote(season.status)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SeasonHeaderSection;